/**
 * Pairing, identity and the heartbeat.
 *
 * Pairing is still one tap and still a `?key=` link — the decision the migration explicitly kept.
 * What differs is only how the link arrives:
 *
 *   web     https://host/?key=…   the key is stored and stripped from the URL before anything is
 *                                 kept in history (the LaunchPad capture pattern)
 *   native  hunsy://?key=…&api=…  or an https link, whose origin IS the API base — a phone has no
 *                                 same-origin to fall back on, so the link has to say where the
 *                                 server is, and it is remembered beside the key
 *
 * Every /api/me doubles as the last_seen_at heartbeat the nudge engine reads for inactivity.
 */
import * as Linking from 'expo-linking';
import { AppState, Platform } from 'react-native';
import { api } from './api';
import { isNavigable, type ScreenId } from './registry';
import { useStore, type Identity } from './store';

interface PairingLink {
  key: string;
  apiBase?: string;
}

/** Reads a pairing link from wherever this platform delivers one. Never throws. */
async function initialLink(): Promise<PairingLink | null> {
  try {
    const href = Platform.OS === 'web' ? globalThis.location?.href : await Linking.getInitialURL();
    if (!href) return null;
    const { queryParams, scheme, hostname, path } = Linking.parse(href);
    const key = typeof queryParams?.key === 'string' ? queryParams.key : null;
    if (!key) return null;

    if (Platform.OS === 'web') return { key };

    const declared = typeof queryParams?.api === 'string' ? queryParams.api : null;
    // An https universal link already names the server; a hunsy:// link has to carry `api=`.
    const fromOrigin = scheme && scheme.startsWith('http') && hostname ? `${scheme}://${hostname}` : null;
    return { key, apiBase: declared ?? fromOrigin ?? undefined };
  } catch {
    return null;
  }
}

/** Take the key out of the address bar so it is never in history, a screenshot or a share. */
function stripKeyFromUrl(): void {
  if (Platform.OS !== 'web') return;
  try {
    const url = new URL(globalThis.location!.href);
    if (!url.searchParams.has('key')) return;
    url.searchParams.delete('key');
    globalThis.history?.replaceState(null, '', url.pathname + url.search + url.hash);
  } catch {
    /* no history API */
  }
}

/**
 * A tapped notification asks for a panel. The web service worker opens `/?panel=missions`; the
 * native handler passes the same module id through openPanel below.
 */
function openRequestedPanel(): void {
  if (Platform.OS !== 'web') return;
  try {
    const requested = new URL(globalThis.location!.href).searchParams.get('panel');
    if (requested) openPanel(requested);
  } catch {
    /* no URL API */
  }
}

export function openPanel(module: string): void {
  // Navigable, not merely known: a push for a panel with no tab must not strand the app on a
  // screen the nav cannot get back from.
  if (isNavigable(module)) useStore.getState().go(module as ScreenId);
}

export async function boot(): Promise<void> {
  const store = useStore.getState();
  await api.load();
  openRequestedPanel();

  const link = await initialLink();
  if (link) {
    await api.pair(link.key, link.apiBase);
    stripKeyFromUrl();
    store.setJustPaired(true);
  }

  // Debug mode: ask who is playing — ONCE per device, not on every open. A real ?key= link still
  // wins, so a pairing link is never intercepted, and the identities are fetched either way
  // because that list is also what puts Switch on Home. When the endpoint is off it 404s and we
  // fall straight through.
  if (!link) {
    const picks = await api.get<Identity[]>('/api/debug/identities').catch(() => null);
    if (picks?.length) {
      store.setIdentities(picks);
      if (!api.configured) {
        store.setSession('pick');
        return; // choose() resumes the boot
      }
    }
  }

  await start();

  // A stored key the server no longer knows is a rotated key, not an unpaired device. With the
  // picker on there is a better answer than asking for a key nobody has: ask who is playing.
  const after = useStore.getState();
  if (after.session === 'badkey' && after.identities.length) after.setSession('pick');
}

/** Adopt one of the debug identities and carry on booting as them. */
export async function choose(identity: Identity): Promise<void> {
  await api.pair(identity.key);
  useStore.setState({ session: 'boot', me: null });
  await start();
}

/**
 * Pair by typing the key instead of opening a link.
 *
 * A phone cannot always be handed a link: a build opened from a QR code in Expo Go never sees
 * one, and the debug picker — the other way in — is a server switch that is meant to be turned
 * off. Without this, the only route onto a phone is a switch nobody should leave on.
 */
export async function pairWithKey(rawKey: string): Promise<boolean> {
  const key = rawKey.trim();
  if (!key) return false;
  await api.pair(key);
  useStore.setState({ session: 'boot', me: null });
  await start();
  const state = useStore.getState().session;
  if (state !== 'ready') await api.unpair(); // a bad key must not stick to the device
  return state === 'ready';
}

let beating = false;

async function start(): Promise<void> {
  const store = useStore.getState();
  if (!api.configured) {
    store.setSession('unpaired');
    return;
  }

  await store.refreshMe();
  if (useStore.getState().session === 'ready') {
    void store.refreshBadges();
    void store.refreshInvites();
  }

  if (beating) return;
  beating = true;

  // Heartbeat: app open, return to the foreground, and a slow tick while visible.
  const beat = () => {
    if (AppState.currentState !== 'active') return;
    const s = useStore.getState();
    void s.refreshMe();
    void s.refreshBadges();
    void s.refreshInvites();
  };
  AppState.addEventListener('change', (state) => {
    if (state === 'active') beat();
  });
  setInterval(beat, 5 * 60_000);
}
